<!-- ClientConceptsView.vue -->
<template>
    <div class="app-layout">
        <!-- Barra lateral -->
        <NavBar :collapsed="collapsed" :pinned="pinned" @update:collapsed="updateCollapsed"
            @update:pinned="updatePinned" />

        <!-- Contenido -->
        <main :class="{ 'main-collapsed': collapsed, 'main-expanded': !collapsed || pinned }">
            <BarTop />

            <div class="p-5 pt-2">
                <!-- título + botón -->
                <div class="py-4 d-flex justify-content-between align-items-center">
                    <h2 class="mb-0">Conceptos del cliente</h2>
                    <button class="btn btn-success" @click="openModal(false)">
                        Agregar concepto
                    </button>
                </div>

                <!-- tabla vacía -->
                <div v-if="rows.length === 0" class="text-center mt-5 text-black">
                    <h3>No hay conceptos ingresados</h3>
                </div>

                <!-- buscador + tabla -->
                <div v-else>
                    <div class="mb-4">
                        <input v-model="search" type="text" class="form-control max-w-xs"
                            placeholder="Buscar conceptos…" />
                    </div>

                    <vue3-datatable :rows="rows" :columns="columns" :search="search" order :sortable="true">
                        <template #concept="data">
                            <span>{{ data.value.concept.name }}</span>
                        </template>
                        <template #currency="data">
                            <span>{{ data.value.currency.name }}</span>
                        </template>
                        <!-- columna estado -->
                        <template #status="slot">
                            {{ slot.value.is_activate ? 'Activado' : 'Desactivado' }}
                        </template>

                        <!-- fecha -->
                        <template #created_at="slot">
                            {{ formattedDate(slot.value.created_at) }}
                        </template>
                        <template #updated_at="slot">
                            {{ formattedDate(slot.value.updated_at) }}
                        </template>
                        <!-- acciones -->
                        <template #actions="slot">
                            <button class="btn" @click="openModal(true, slot.value)">
                                <i class="bi bi-pencil px-2"></i>
                            </button>
                            <button class="btn" @click="removeRow(slot.value.id_client_concept)">
                                <i class="bi bi-trash px-2"></i>
                            </button>
                            <button class="btn" @click="showLog(slot.value.id_client_concept)">
                                <i class="bi bi-clock-history px-2"></i> <!-- icono historial -->
                            </button>
                        </template>
                    </vue3-datatable>
                </div>
            </div>
        </main>
        <!-- Modal LOG -->
        <div v-show="showLogModal" class="modal-mb">
            <div class="modal-dialog bg-white">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Historial de valores</h5>
                        <button class="btn-close" @click="showLogModal = false"></button>
                    </div>

                    <div class="modal-body">
                        <table class="table">
                            <thead>
                                <tr>
                                    <th>Fecha</th>
                                    <th>Moneda</th>
                                    <th class="text-end">Valor anterior</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="l in logRows" :key="l.id">
                                    <td>{{ formattedDate(l.created_at) }}</td>
                                    <td class="text-center">{{ l.currency.code }}</td>
                                    <td class="text-end">{{ l.value }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div class="modal-footer">
                        <button class="btn btn-outline-secondary" @click="showLogModal = false">
                            Cerrar
                        </button>
                    </div>
                </div>
            </div>
        </div>
        <!-- Modal alta / edición -->
        <div v-show="showModal" class="modal-mb">
            <div class="modal-dialog bg-white">
                <div class="modal-content">
                    <!-- header -->
                    <div class="modal-header">
                        <h5 class="modal-title">
                            {{ isEdit ? 'Editar concepto del cliente' : 'Añadir concepto al cliente' }}
                        </h5>
                        <button class="btn-close" @click="closeModal"></button>
                    </div>

                    <!-- body -->
                    <div class="modal-body mt-3">
                        <!-- selector de concepto -->
                        <div class="mb-3">
                            <label class="form-label">Concepto</label>
                            <select v-model="form.id_concept" class="form-select"
                                :class="{ 'border-danger': errors.id_concept }" @blur="validateField('id_concept')"
                                :disabled="isEdit">
                                <option value="" disabled>Seleccione un concepto</option>
                                <option v-for="c in concepts" :key="c.id" :value="c.id">{{ c.name }}</option>
                            </select>
                            <small v-if="errors.id_concept" class="text-danger">Campo requerido</small>
                        </div>

                        <!-- moneda -->
                        <div class="mb-3">
                            <label class="form-label">Moneda</label>
                            <select v-model="form.id_currency" class="form-select"
                                :class="{ 'border-danger': errors.id_currency }" 
                                @blur="validateField('id_currency')" :disabled="isEdit">
                                <option value="" disabled>Seleccione moneda</option>
                                <option v-for="m in currencies" :key="m.id_currency" :value="m.id_currency">
                                    {{ m.name }}
                                </option>
                            </select>
                            <small v-if="errors.id_currency" class="text-danger">Campo requerido</small>
                        </div>

                        <!-- valor -->
                        <div class="mb-3">
                            <label class="form-label">Valor</label>
                            <input v-model.number="form.value" type="number" step=".01" class="form-control"
                                :class="{ 'border-danger': errors.value }" @blur="validateField('value')" />
                            <small v-if="errors.value" class="text-danger">Valor requerido</small>
                        </div>
                    </div>

                    <!-- footer -->
                    <div class="modal-footer">
                        <button v-if="!isEdit" class="btn btn-secondary" @click="saveRow">
                            Guardar
                        </button>
                        <button v-if="isEdit" class="btn btn-secondary" @click="updateRow">
                            Actualizar
                        </button>
                        <button class="btn btn-outline-secondary" @click="closeModal">
                            Cerrar
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { ref } from "vue";
import Vue3Datatable from "@bhplugin/vue3-datatable";
import "@bhplugin/vue3-datatable/dist/style.css";
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import NavBar from './../component/NavBar.vue';
import BarTop from './../component/BarTop.vue';

export default {
    components: { Vue3Datatable, NavBar, BarTop },

    data() {
        return {
            /* layout */
            collapsed: true,
            pinned: localStorage.getItem('pinned') === 'false' ? false : true,
            theme: localStorage.getItem('theme') || 'light-mode',

            /* tabla */
            rows: [],
            search: '',
            columns: [
                { field: 'created_at', title: 'Fecha Creacion' },
                { field: 'updated_at', title: 'Fecha Actualizacion' },
                { field: 'concept', title: 'Nombre concepto', filter: false },
                { field: 'currency', title: 'Divisa', filter: false },
                { field: 'value', title: 'valor', filter: false },

                { field: 'actions', title: '', sortable: false },
            ],
            showLogModal: false,
            logRows: [],
            /* modal */
            showModal: false,
            isEdit: false,
            editId: null,

            /* listas auxiliares */
            concepts: [],
            currencies: [],

            /* formulario */
            form: {
                id_concept: '',
                id_currency: '',
                value: null,
            },
            errors: {
                id_concept: false,
                id_currency: false,
                value: false,
            },
        }
    },

    mounted() {
        document.body.className = this.theme
        const parts = window.location.pathname.split('/')
        this.clientId = Number(parts[3] || 0)          // asume /clients/{id}/concepts
        this.loadData()
    },

    methods: {
        async showLog(idClientConcept) {
            try {
                const res = await axios.get(`/api/concepts/${idClientConcept}/logs`, {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem('accessToken')}`
                    }
                });
                this.logRows = res.data        // [{value, id_currency, created_at, currency:{code}}]
                this.showLogModal = true
            } catch (e) {
                console.error(e)
            }
        },
        /* ─ helpers UI ────────────────────────────────────────────── */
        formattedDate(dateF) {
            const d = new Date(dateF)
            const p = (n) => String(n).padStart(2, '0')
            return `${p(d.getDate())}-${p(d.getMonth() + 1)}-${d.getFullYear()} ${p(d.getHours())}:${p(
                d.getMinutes()
            )}`
        },
        updateCollapsed(v) {
            this.collapsed = v
        },
        updatePinned(v) {
            this.pinned = v
        },

        /* ─ validaciones ─────────────────────────────────────────── */
        validateField(field) {
            this.errors[field] = !this.form[field]
            return !this.errors[field]
        },
        validateForm() {
            return (
                this.validateField('id_concept') &
                this.validateField('id_currency') &
                this.validateField('value')
            )
        },

        /* ─ modal ───────────────────────────────────────────────── */
        openModal(edit = false, row = null) {
            this.isEdit = edit
            if (edit && row) {
                this.editId = row.id_client_concept
                this.form.id_concept = row.id_concept
                this.form.id_currency = row.id_currency
                this.form.value = row.value
            } else {
                this.editId = null
                this.form.id_concept = ''
                this.form.id_currency = ''
                this.form.value = null
            }
            Object.keys(this.errors).forEach((k) => (this.errors[k] = false))
            this.showModal = true
        },
        closeModal() {
            this.showModal = false
        },

        /* ─ peticiones ──────────────────────────────────────────── */
        async loadData() {
            try {
                /* conceptos globales */

                const conceptsRes = await axios.get('/api/conceptsAll/', {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem('accessToken')}`
                    }
                });
                this.concepts = conceptsRes.data;

                /* monedas */
                const curRes = await axios.get('/api/currencies', {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem('accessToken')}`
                    }
                }); // usa tu endpoint real
                this.currencies = curRes.data.filter(f=> f.status == 1);

                /* asociaciones del cliente */
                const rowsRes = await axios.get(`/api/clients/${this.clientId}/concepts`, {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem('accessToken')}`
                    }
                });
                this.rows = rowsRes.data
            } catch (e) {
                console.error(e)
            }
        },

        async saveRow() {
            if (!this.validateForm()) return
            try {
                await axios.post(`/api/clients/${this.clientId}/concepts`, this.form, {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem('accessToken')}`
                    }
                });
                await this.loadData()
                this.closeModal()
            } catch (e) {
                console.error(e)
            }
        },

        async updateRow() {
            if (!this.validateForm()) return
            try {
                const resp =await axios.put(
                    `/api/clients/${this.clientId}/concepts/${this.editId}`,
                    this.form, {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem('accessToken')}`
                    }
                });
                this.$swal.fire({
                        icon: "info",
                        title: "Concepto actualizado con exito",
                        confirmButtonText: "Ok"
                    });
                await this.loadData()
                this.closeModal()
            } catch (e) {
                console.error(e)
            }
        },

        async removeRow(id) {
            const ok = confirm('¿Eliminar este concepto?')
            if (!ok) return
            try {
                await axios.delete(`/api/clients/${this.clientId}/concepts/${id}`, {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem('accessToken')}`
                    }
                });
                
                await this.loadData()
            } catch (e) {
                console.error(e)
            }
        },
    },
}
</script>

<style>
/* estilos existentes – los dejamos tal cual */
.list-group-item2 {
    position: relative;
    padding: var(--bs-list-group-item-padding-y) var(--bs-list-group-item-padding-x);
    color: var(--bs-list-group-color);
    background-color: var(--bs-list-group-bg);
    border: var(--bs-list-group-border-width) solid var(--bs-list-group-border-color);
}

.bh-table-responsive input[type='checkbox'] {
    width: 2rem;
    height: 1rem;
    opacity: 1;
    position: inherit;
}

/* overlay modal */
.modal-mb {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1050;
}

.modal-dialog {
    max-width: 600px;
}
</style>